import { RoutePaths } from "@/config/route-paths";
import type { Credential } from "@/features/credentials/models/credential";
import { useDeleteCredentialMutation } from "@/features/credentials/queries/use-delete-credential";
import { Button, DeleteConfirmationDialog, Hint } from "@cmde/shadcn";
import { InfoIcon, PencilIcon, Trash2Icon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";

export function CredentialRowActions({
  credential,
  customerId,
  organizationId,
  sport,
}: {
  credential: Credential;
  customerId: number;
  organizationId: string;
  sport: string;
}) {
  const { t } = useTranslation();
  const deleteCredentialMutation = useDeleteCredentialMutation();
  const showAccessInfo = credential.credentialType === "ENTITY" || credential.credentialType === "VENUE";

  const handleDelete = () => {
    deleteCredentialMutation.mutate(
      {
        sport,
        organizationId,
        credentialId: String(credential.credentialId),
      },
      {
        onSuccess: () => {
          toast.success(t("credentials.delete.success"));
        },
        onError: (error) => {
          const message =
            error instanceof Error ? error.message : t("credentials.delete.errors.submit_failed");
          toast.error(message);
        },
      },
    );
  };

  return (
    <div className="flex items-center justify-end gap-2">
      {showAccessInfo ? (
        <Hint content={t("credentials.list.actions.info")}>
          <Button variant="outline" size="icon-sm" asChild>
            <Link
              to={RoutePaths.credentials.info({
                customerId,
                organizationId,
                sport,
                credentialId: String(credential.credentialId),
              })}
              aria-label={t("credentials.list.actions.info")}
            >
              <InfoIcon className="size-3.5" />
            </Link>
          </Button>
        </Hint>
      ) : null}
      <Hint content={t("credentials.list.actions.edit")}>
        <Button variant="outline" size="icon-sm" asChild>
          <Link
            to={RoutePaths.credentials.edit({
              customerId,
              organizationId,
              sport,
              credentialId: String(credential.credentialId),
            })}
            aria-label={t("credentials.list.actions.edit")}
          >
            <PencilIcon className="size-3.5" />
          </Link>
        </Button>
      </Hint>
      <DeleteConfirmationDialog
        title={t("credentials.delete.title")}
        description={t("credentials.delete.description", { name: credential.name })}
        confirmLabel={t("credentials.delete.confirm")}
        cancelLabel={t("credentials.delete.cancel")}
        confirmVariant="destructive"
        onConfirm={handleDelete}
      >
        <Button
          type="button"
          variant="outline-destructive"
          size="icon-sm"
          aria-label={t("credentials.list.actions.delete")}
          disabled={deleteCredentialMutation.isPending}
        >
          <Trash2Icon className="size-3.5" />
        </Button>
      </DeleteConfirmationDialog>
    </div >
  );
}
